"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FullScaleProduction() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const services = [{
    index: "01",
    title: "CREATIVE DIRECTION",
    description: "We architect visual identities from the ground up. Our process begins with deep-dive mood boards and narrative treatments, ensuring every frame aligns with your brand's core mission.",
    linkLabel: "VIEW OUR WORK",
    linkTo: "/OurWorkPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_direction_moodboard.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_direction_moodboard.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_direction_moodboard.png-wvc-sizes",
      alt: "Creative director reviewing mood boards and script treatments"
    },
    imagePosition: "left" as const
  }, {
    index: "02",
    title: "FULL-SCALE PRODUCTION",
    description: "From high-end commercial video spots to social-first content scaling, we manage the entire production lifecycle. Our team delivers cinematic quality with strategic precision.",
    linkLabel: "OUR EXPERTISE",
    linkTo: "/ExpertisePage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/production_set_filming.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/production_set_filming.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/production_set_filming.png-wvc-sizes",
      alt: "Professional film crew on set capturing a commercial spot"
    },
    imagePosition: "right" as const
  }];
  return <section data-section-id="2162" id="full-scale-production" className="bg-background text-foreground relative min-h-[80vh] overflow-hidden py-20 lg:py-28">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
      backgroundImage: "repeating-linear-gradient(135deg, var(--foreground) 0px, var(--foreground) 1px, transparent 1px, transparent 24px), repeating-linear-gradient(135deg, var(--foreground) 0px, var(--foreground) 1px, transparent 1px, transparent 72px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {}
          <div className="lg:col-span-5 xl:col-span-4">
            <div ref={leftRef} className={`lg:sticky lg:top-24 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div className="mb-10 flex items-center gap-3 font-mono text-xs tracking-widest text-muted-foreground">
                <span className="h-px w-8 bg-primary" />
                <span>[ SERVICES ]</span>
              </div>

              {}
              <h2 className="font-default font-bold leading-[0.85] tracking-tight">
                <span className="block text-6xl text-foreground sm:text-7xl lg:text-7xl xl:text-8xl">
                  VIVID
                </span>
                <span className="block text-6xl sm:text-7xl lg:text-7xl xl:text-8xl" style={{
                WebkitTextStroke: "1.5px var(--foreground)",
                color: "transparent"
              }}>
                  STUDIO
                </span>
              </h2>

              {}
              <p className="font-default text-lg lg:text-xl leading-relaxed text-muted-foreground mt-8 max-w-md">
                Vivid Studio is your partner in visual storytelling. We bridge the gap between initial concept and final execution, creating high-impact campaigns that resonate across every platform.
              </p>

              {}
              <div className="mt-10 flex flex-col gap-3 max-w-xs">
                <Button asChild variant="outline" size="lg" className="font-default justify-between bg-transparent border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary tracking-wider text-xs h-12 px-5">
                  <Link to="/OurWorkPage">
                    EXPLORE CAMPAIGNS
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-default justify-between bg-transparent border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary tracking-wider text-xs h-12 px-5">
                  <Link to="/StartProjectPage">
                    DOWNLOAD CAPABILITIES
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 xl:col-start-7 xl:col-span-6 flex flex-col gap-8 lg:gap-10">
            {services.map((service, i) => {
            const revealRef = i === 0 ? card1Ref : card2Ref;
            const revealVisible = i === 0 ? card1Visible : card2Visible;
            return <div key={service.index} data-index={i} ref={revealRef} className={`group relative bg-card backdrop-blur-md border border-border shadow-lg transition-all duration-700 hover:-translate-y-1 lg:min-h-[50vh] ${revealVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              backdropFilter: "blur(12px)"
            }}>
                  {}
                  <div aria-hidden="true" className="absolute top-0 left-0 h-px bg-primary w-0 group-hover:w-full transition-all duration-700 ease-out" />

                  <div className="grid h-full grid-cols-1 md:grid-cols-2">
                    {}
                    <div className={`relative overflow-hidden min-h-[260px] md:min-h-[400px] ${service.imagePosition === "right" ? "md:order-2" : "md:order-1"}`}>
                      <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-background/60 via-background/10 to-transparent" />
                    </div>

                    {}
                    <div className={`flex flex-col justify-between p-8 lg:p-10 ${service.imagePosition === "right" ? "md:order-1" : "md:order-2"}`}>
                      {}
                      <div className="font-default font-bold leading-none" style={{
                    fontSize: "72px",
                    WebkitTextStroke: "1.5px var(--primary)",
                    color: "transparent"
                  }}>
                        {service.index}
                      </div>

                      <div className="mt-6 md:mt-0">
                        {}
                        <h3 className="font-default font-bold text-2xl sm:text-3xl tracking-tight text-foreground">
                          {service.title}
                        </h3>

                        {}
                        <p className="font-default text-base lg:text-lg leading-relaxed text-muted-foreground mt-4 line-clamp-3">
                          {service.description}
                        </p>

                        {}
                        <Link to={service.linkTo} className="mt-8 inline-flex items-center gap-3 font-default font-medium text-xs tracking-widest text-primary hover:gap-5 transition-all">
                          <ArrowRight className="size-4" />
                          {service.linkLabel}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}